import { Block, isPrestation, isSection } from '../types';

/**
 * Convertit les blocs de chiffrage en format CSV
 * @param blocks - Liste des blocs à exporter (doit être dans l'ordre d'affichage de ChiffWorkArea)
 * @param nomProjet - Nom du projet pour le fichier
 * @returns Le contenu CSV sous forme de string
 */
export const exportToCSV = (blocks: Block[], nomProjet: string): string => {
  // En-têtes du CSV
  const headers = [
    'Descriptif',
    'Unité',
    'Quantité',
    'Prix unitaire',
    'Prix total'
  ];

  // Extraire toutes les lignes (sections et prestations) dans l'ordre exact d'affichage
  // Les blocs sont déjà dans l'ordre d'affichage grâce à getBlocksInOrder() de BlockEditor
  const allRows: string[][] = [];

  blocks.forEach(block => {
    if (isSection(block)) {
      const content = block.content;
      
      // Ajouter la section avec des colonnes vides pour les données de prestation
      allRows.push([
        content.titre || '',
        '', // Unité vide pour les sections
        '', // Quantité vide pour les sections
        '', // Prix unitaire vide pour les sections
        ''  // Prix total vide pour les sections
      ]);
    } else if (isPrestation(block)) {
      const content = block.content;
      
      // Nettoyer et formater les valeurs
      const description = content.intitule || '';
      const unite = content.unite || '';
      const quantite = content.quantite || '1';
      const prixUnitaire = content.prixUnitaire || '0';
      
      // Calculer le prix total si pas défini
      let prixTotal = content.prixTotal;
      if (!prixTotal) {
        const prixUnitaireNum = parseFloat(prixUnitaire.replace(/[^\d.,]/g, '').replace(',', '.') || '0');
        const quantiteNum = parseFloat(quantite || '1');
        prixTotal = Math.round(prixUnitaireNum * quantiteNum).toString();
      }

      // Nettoyer le prix total
      prixTotal = prixTotal.replace(/[^\d.,]/g, '').replace(',', '.');

      allRows.push([
        description,
        unite,
        quantite,
        prixUnitaire,
        prixTotal
      ]);
    }
  });

  // Créer le contenu CSV
  const csvContent = [
    headers.join(','),
    ...allRows.map(row => 
      row.map(cell => 
        // Échapper les virgules et guillemets dans les cellules
        `"${cell.replace(/"/g, '""')}"`
      ).join(',')
    )
  ].join('\n');

  return csvContent;
};

/**
 * Télécharge un fichier CSV
 * @param content - Contenu du fichier CSV
 * @param filename - Nom du fichier (sans extension)
 */
export const downloadCSV = (content: string, filename: string): void => {
  // Nettoyer le nom du fichier pour qu'il soit valide
  const cleanFilename = filename
    .replace(/[^a-zA-Z0-9\s\-_]/g, '') // Supprimer les caractères spéciaux
    .replace(/\s+/g, '_') // Remplacer les espaces par des underscores
    .toLowerCase();

  // Créer le blob et le lien de téléchargement
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${cleanFilename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Fonction principale pour exporter le chiffrage en CSV
 * @param blocks - Liste des blocs à exporter
 * @param nomProjet - Nom du projet
 */
export const exportChiffrageToCSV = (blocks: Block[], nomProjet: string): void => {
  try {
    const csvContent = exportToCSV(blocks, nomProjet);
    downloadCSV(csvContent, nomProjet);
    console.log('Export CSV réussi pour le projet:', nomProjet);
  } catch (error) {
    console.error('Erreur lors de l\'export CSV:', error);
    alert('Erreur lors de l\'export CSV. Veuillez réessayer.');
  }
};
